import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Plus,
  MessageSquare,
  Upload,
  Activity,
  Sparkles,
} from 'lucide-react';
import {
  A2UISurface,
  ChatMessage,
  computeThreadStats,
} from '../components/ChatComponents';
import {
  TabBar,
  ErrorBanner,
  InfoTabPanel,
  ThreadInfoPanel,
  MemoriesTabPanel,
  DocumentsTabPanel,
  HealthTabPanel,
} from '../components/TabPanels';
import type { TabId } from '../components/TabPanels';
import { agentApi, ChatHttpError } from '../api';
import type { ChatRequest } from '../api';
import type { SurfaceActionPayload } from '../utils/runReport';
import type { ErrorFlags } from '../hooks/errorDispatch';
import { dispatchByErrorCode } from '../hooks/errorDispatch';
import type { HealthAnalysisListItem } from '../api/agent';
import { threadsApi } from '../api/threads';
import { useAuth } from '../contexts/AuthContext';
import {
  useProfileEditor,
  useTierManager,
  useTabData,
  useFileAttachment,
  toFileAttachment,
  useMessageEditing,
  useArchiveLoader,
  usePlaygroundUsers,
  useThreadManager,
  useChatStream,
  useThreadDetail,
} from '../hooks';
import ToolCallPanel from '../components/playground/ToolCallPanel';
import SSELogPanel from '../components/playground/SSELogPanel';
import PlaygroundUserList from '../components/playground/PlaygroundUserList';
import PlaygroundThreadList from '../components/playground/PlaygroundThreadList';
import SharingDialog from '../components/playground/SharingDialog';
import VirtualizedMessageList from '../components/playground/VirtualizedMessageList';
import ChatInputArea from '../components/playground/ChatInputArea';
import ChatMessageRenderer from '../components/playground/ChatMessageRenderer';
import CalendarTabPanel from '../components/CalendarTabPanel';
import { AvailableToolsSection } from '../components/AvailableToolsSection';
import { useAvailableTools } from '../hooks/useAvailableTools';

// Profile templates
const PROFILE_TEMPLATES = {
  user: {
    name: '홍길동',
    birth_date: '1995-03-15',
    gender: 'male',
    language: 'ko',
    height_cm: 175,
    blood_type: 'A',
  },
  device: {
    platform: 'ios',
    timezone: 'Asia/Seoul',
  },
  custom: {
    medical_conditions: ['고혈압'],
    allergies: [],
    medications: [],
  },
};

type Message = ChatMessage;

const PlaygroundChat: React.FC = () => {
  // Auth context
  const { user: adminUser } = useAuth();

  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('user');
  const threadIdFromUrl = searchParams.get('thread');
  const tabFromUrl = searchParams.get('tab') as 'chat' | 'info' | 'memories' | 'documents' | 'health' | null;

  // Shared error state (declared early for hooks that need it)
  const [error, setError] = useState<string | null>(null);

  // User list state
  const [selectedUserId, setSelectedUserIdState] = useState<string | null>(userIdFromUrl);
  const playgroundUsers = usePlaygroundUsers({ adminUserId: adminUser?.id, onError: setError });
  const {
    users, usersLoading, selectedUser, loadUsers, loadUserDetails,
    handleTogglePinUser, openSharingDialog, handleSaveSharing,
    sharingDialogUserId, setSharingDialogUserId,
    adminUsersList, selectedAdminIds, setSelectedAdminIds,
    shareToAll, setShareToAll, sharingLoading,
  } = playgroundUsers;

  // Thread state
  const [selectedThreadId, setSelectedThreadIdState] = useState<string | null>(threadIdFromUrl);
  const threadMgr = useThreadManager({ selectedUserId, onError: setError });
  const {
    threads, setThreads, threadsLoading,
    pinnedThreadIds, togglePinThread, sortedThreads,
    threadSummary, setThreadSummary, threadStats, setThreadStats,
    summaryExpanded, setSummaryExpanded,
  } = threadMgr;

  const { data: threadDetail, isLoading: threadDetailLoading, refetch: refetchThreadDetail } = useThreadDetail(selectedThreadId);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [archiveTotal, setArchiveTotal] = useState(0);

  // URL param sync
  const updateUrlParams = (userId: string | null, threadId: string | null, tab?: string) => {
    const params: Record<string, string> = {};
    if (userId) params.user = userId;
    if (threadId) params.thread = threadId;
    if (tab) params.tab = tab;
    setSearchParams(params, { replace: true });
  };

  const setSelectedUserId = (userId: string | null) => {
    setSelectedUserIdState(userId);
    setSelectedThreadIdState(null);
    threadMgr.reloadPinnedThreads(userId);
    updateUrlParams(userId, null);
  };

  const setSelectedThreadId = (threadId: string | null) => {
    setSelectedThreadIdState(threadId);
    updateUrlParams(selectedUserId, threadId);
  };

  // Side-panel focus: when the user clicks [자세히] on a tool summary badge we
  // filter ToolCallPanel to that message's tools. null = show everything.
  const [focusedToolMessageId, setFocusedToolMessageId] = useState<string | null>(null);

  // Quota guardrail — set by QUOTA_EXCEEDED dispatch; cleared after Retry-After.
  const [retryAfterBlock, setRetryAfterBlock] = useState(false);

  // Structured chat-error handler — bridges useChatStream's ErrorFlags-aware
  // onError signature to the concrete UX actions (block send, cancel edit mode,
  // clear attachments) instead of losing them to a generic error message.
  const handleChatError = useCallback(
    (detail: string, flags?: ErrorFlags) => {
      setError(detail);
      if (flags?.blockSend || flags?.retryAfter) {
        setRetryAfterBlock(true);
        if (typeof flags.retryAfter === 'number') {
          // Lift the block once Retry-After elapses so the UI auto-recovers.
          setTimeout(() => setRetryAfterBlock(false), flags.retryAfter * 1000);
        }
      }
      if (flags?.cancelEditMode) {
        setEditingMessageId(null);
        setEditingContent('');
      }
      if (flags?.clearAttachments) {
        setAttachedFiles([]);
      }
    },
    // editing/attachment setters are captured from closure; stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Chat stream (core state + SSE processor)
  const chatStream = useChatStream({ onError: handleChatError });
  const {
    messages, setMessages, input, setInput,
    isStreaming, setIsStreaming, isPendingResponse, setIsPendingResponse,
    pendingRetryRef, promptLabel, setPromptLabel, promptLabels,
    thoughtStep, setThoughtStep,
    toolCalls, setToolCalls, toolPanelExpanded, setToolPanelExpanded,
    sseLog,
    serverVersion, lastToolUiMsgId,
    processStreamEvents, loadPromptLabels, loadServerVersion,
    handleAbort,
  } = chatStream;
  // SSE log panel toggle (default collapsed; debug-oriented panel).
  const [sseLogExpanded, setSSELogExpanded] = useState(false);

  // Edit / copy message
  const editing = useMessageEditing();
  const { editingMessageId, editingContent, copiedMessageId, setEditingMessageId, setEditingContent, handleStartEdit, handleCancelEdit, handleCopyMessage } = editing;

  // Archive progressive loading
  const archive = useArchiveLoader({
    selectedThreadId,
    setMessages,
    setIsPendingResponse,
    pendingRetryRef,
  });
  const {
    setArchiveOffset,
    hasMoreArchive, setHasMoreArchive,
    loadingArchive, isLoadingArchiveRef,
    loadMoreArchive, archiveCacheKey, archiveMetaCacheKey,
  } = archive;

  // File attachment
  const fileAttachment = useFileAttachment({ userId: selectedUserId ?? '', onError: setError });
  const { attachedFiles, setAttachedFiles, fileInputRef, handleFileAttach, removeAttachedFile, attachDocumentId } = fileAttachment;

  // Health analysis request state (Playground-specific)
  const [healthContext, setHealthContext] = useState('');
  const [isRequestingAnalysis, setIsRequestingAnalysis] = useState(false);

  // Tools state
  const { availableTools, toolsLoading, loadAvailableTools } = useAvailableTools();

  // Tab state
  const [activeTab, setActiveTabState] = useState<TabId>((tabFromUrl as TabId) || 'chat');

  const setActiveTab = (tab: TabId) => {
    setActiveTabState(tab);
    updateUrlParams(selectedUserId, selectedThreadId, tab !== 'chat' ? tab : undefined);
  };

  // Load users, tiers, and server version on mount
  useEffect(() => {
    loadUsers();
    tier.loadTiers();
    loadServerVersion();
    loadPromptLabels();
  }, [adminUser?.id]);

  // Load user details and threads when user selected
  useEffect(() => {
    if (selectedUserId) {
      // 필수: 항상 로드
      loadUserDetails(selectedUserId);
      loadThreads(selectedUserId);
      // 탭 데이터 초기화 (이전 유저 데이터 제거)
      tabData.setMemories([]);
      tabData.setDocuments([]);
      tabData.setHealthAnalyses([]);
      // 현재 info 탭이면 도구 목록 로드 (URL 직접 진입 또는 유저 변경 시)
      if (activeTab === 'info') loadAvailableTools();
    }
  }, [selectedUserId]);

  // Load messages when thread selected
  useEffect(() => {
    if (pendingRetryRef.current) clearTimeout(pendingRetryRef.current);
    setIsPendingResponse(false);
    if (selectedThreadId) {
      loadThreadMessages(selectedThreadId);
    } else {
      setMessages([]);
      setThreadSummary(null);
      setThreadStats(null);
      setHasMoreArchive(false);
      setArchiveOffset(0);
    }
    return () => {
      if (pendingRetryRef.current) clearTimeout(pendingRetryRef.current);
    };
  }, [selectedThreadId]);

  // Custom hooks
  const profile = useProfileEditor({ selectedUser, selectedUserId, onProfileSaved: loadUserDetails, onError: setError });
  const tier = useTierManager({ selectedUserId, onTierChanged: loadUserDetails, onError: setError });
  const tabData = useTabData({ selectedUserId, onError: setError });

  const loadThreads = (userId: string) =>
    threadMgr.loadThreads(userId, {
      selectedThreadId,
      onAutoSelect: (threadId) => {
        setSelectedThreadIdState(threadId);
        updateUrlParams(userId, threadId);
      },
    });

  const loadThreadMessages = async (threadId: string, { skipPendingDetection = false } = {}) => {
    try {
      // admin context이므로 항시 thoughts opt-in.
      // 일반 BE 클라이언트는 include 미지정 → thoughts 페이로드 부담 없음.
      const response = await threadsApi.getMessages(threadId, {
        order: 'asc',
        source: 'active',
        limit: 1000,
        include: 'thoughts',
      });
      const activeMessages: Message[] = response.messages
        .filter((msg) => ['human', 'user', 'ai', 'assistant'].includes(msg.role) && (msg.content || '').trim())
        .map((msg, index) => ({
          id: msg.id || `msg-${index}`,
          role: msg.role === 'human' || msg.role === 'user' ? 'user' : 'assistant',
          content: (msg.content || '').trim(),
          timestamp: new Date(msg.timestamp),
          source: msg.source,
          ...(msg.tool_ui ? { toolUi: msg.tool_ui as A2UISurface } : {}),
          ...(msg.metadata ? { metadata: msg.metadata } : {}),
          ...(msg.thoughts && msg.thoughts.length > 0 ? { thoughts: msg.thoughts } : {}),
        }));
      setThreadSummary(response.summary || null);
      setGenerationStatus(response.generation_status || null);

      // Archive progressive loading state
      const archTotal = response.archive_total ?? 0;
      setArchiveTotal(archTotal);

      // Start with active messages only (LangGraph's current state).
      // Archive messages are loaded on-demand via "이전 메시지 보기" button.
      // Clear any cached archive from previous session.
      try {
        sessionStorage.removeItem(archiveCacheKey(threadId));
        sessionStorage.removeItem(archiveMetaCacheKey(threadId));
      } catch {
        /* ignore */
      }

      setMessages(activeMessages);
      setArchiveOffset(0);
      setHasMoreArchive(archTotal > 0);

      setThreadStats(computeThreadStats(activeMessages, archTotal));

      // Update thread list message count
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === threadId ? { ...t, message_count: activeMessages.length + archTotal } : t)),
      );

      // Detect pending AI response: last message is user → background graph may still be running
      // Skip detection after streaming completes (messages are already finalized)
      const lastActive = activeMessages[activeMessages.length - 1];
      if (!skipPendingDetection && lastActive && lastActive.role === 'user') {
        setIsPendingResponse(true);
        // Add placeholder assistant message
        const pendingId = 'pending-response';
        setMessages((prev) => [...prev, { id: pendingId, role: 'assistant', content: '', timestamp: new Date() }]);
        // Auto re-fetch after 3s (background graph typically completes in 2-5s)
        if (pendingRetryRef.current) clearTimeout(pendingRetryRef.current);
        pendingRetryRef.current = setTimeout(() => {
          setIsPendingResponse(false);
          loadThreadMessages(threadId);
        }, 3000);
      } else {
        setIsPendingResponse(false);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
      setThreadSummary(null);
      setThreadStats(null);
      setHasMoreArchive(false);
      setIsPendingResponse(false);
    }
  };

  const handleRequestHealthAnalysis = async () => {
    if (!selectedUserId || !healthContext.trim()) return;

    try {
      setIsRequestingAnalysis(true);
      setError(null);

      let context;
      try {
        context = JSON.parse(healthContext);
      } catch {
        setError('JSON 형식이 올바르지 않습니다.');
        return;
      }

      const response = await agentApi.createHealthAnalysis(selectedUserId, context);
      const result = await agentApi.pollHealthAnalysis(selectedUserId, response.analysis_id, 1000, 60);

      // Prepend new result to list
      const newItem: HealthAnalysisListItem = {
        analysis_id: result.analysis_id,
        status: result.status,
        created_at: result.created_at,
        result: result.result ? { flag: result.result.flag, title: result.result.title } : undefined,
      };
      tabData.setHealthAnalyses((prev) => [newItem, ...prev]);
      setHealthContext('');

      // Auto-expand the new result
      tabData.setExpandedAnalysisId(result.analysis_id);
      tabData.setExpandedAnalysisDetail(result);
    } catch (err) {
      console.error('Failed to request health analysis:', err);
      setError('건강 분석 요청에 실패했습니다.');
    } finally {
      setIsRequestingAnalysis(false);
    }
  };

  // Apply profile template (fills all fields with defaults)
  const applyProfileTemplate = () => {
    const template = {
      user: PROFILE_TEMPLATES.user,
      device: { ...PROFILE_TEMPLATES.device, app_version: serverVersion },
      custom: PROFILE_TEMPLATES.custom,
      settings: selectedUser?.settings || {},
    };
    profile.setEditProfileJson(JSON.stringify(template, null, 2));
  };

  // Auto-scroll is now handled by VirtualizedMessageList

  // Create new user (glue: coordinates hook + component state)
  const handleCreateUser = () =>
    playgroundUsers.handleCreateUser({
      onCreated: (userId) => setSelectedUserId(userId),
      clearMessages: () => setMessages([]),
      clearMemories: () => tabData.setMemories([]),
      resetTab: () => setActiveTabState('chat'),
    });

  // Delete user (glue)
  const handleDeleteUser = (userId: string, e: React.MouseEvent) =>
    playgroundUsers.handleDeleteUser(userId, e, {
      selectedUserId,
      onDeselect: () => setSelectedUserIdState(null),
      clearMessages: () => setMessages([]),
      clearMemories: () => tabData.setMemories([]),
      clearThreads: () => setThreads([]),
    });

  // Clone user (glue) — clone lands in this admin's playground; select it.
  const handleCloneUser = (userId: string, e: React.MouseEvent) =>
    playgroundUsers.handleCloneUser(userId, e, {
      onCloned: (newUserId) => setSelectedUserId(newUserId),
      clearMessages: () => setMessages([]),
      clearMemories: () => tabData.setMemories([]),
      clearThreads: () => setThreads([]),
    });

  // Create new thread (glue)
  const handleCreateThread = () =>
    threadMgr.handleCreateThread({
      onCreated: (threadId) => setSelectedThreadId(threadId),
      clearMessages: () => setMessages([]),
    });

  // Delete thread (glue)
  const handleDeleteThread = (threadId: string, e: React.MouseEvent) =>
    threadMgr.handleDeleteThread(threadId, e, {
      selectedThreadId,
      onDeselect: () => setSelectedThreadIdState(null),
      clearMessages: () => setMessages([]),
    });

  // Clone thread (glue) — clone lands under the same user; select the new thread.
  const handleCloneThread = (threadId: string, e: React.MouseEvent) =>
    threadMgr.handleCloneThread(threadId, e, {
      onCloned: (newThreadId) => setSelectedThreadId(newThreadId),
      clearMessages: () => setMessages([]),
    });

  const handleSend = async (overrideMessage?: string, surfaceAction?: SurfaceActionPayload) => {
    const messageToSend = overrideMessage || input.trim();
    if ((!messageToSend && attachedFiles.length === 0) || !selectedUserId || isStreaming) return;

    const currentFiles = overrideMessage ? [] : [...attachedFiles];
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageToSend || '(파일 첨부)',
      timestamp: new Date(),
      source: 'active',
      files: currentFiles.map((f) => ({
        name: f.name,
        media_type: f.media_type,
        preview: f.preview || undefined,
      })),
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!overrideMessage) {
      setInput('');
      setAttachedFiles([]);
    }
    setIsStreaming(true);
    setError(null);
    setToolCalls([]);

    const aiMsgId = (Date.now() + 1).toString();
    setMessages((prev) => [
      ...prev,
      { id: aiMsgId, role: 'assistant', content: '', timestamp: new Date(), source: 'active' },
    ]);

    try {
      let threadId = selectedThreadId;

      if (!threadId) {
        try {
          const newThread = await threadsApi.create(selectedUserId);
          threadId = newThread.thread_id;
          setSelectedThreadIdState(threadId);
          updateUrlParams(selectedUserId, threadId);
          loadThreads(selectedUserId);
        } catch (err) {
          console.error('Failed to create thread:', err);
          throw new Error('Failed to create chat thread');
        }
      }

      // A card tap sends an empty message: the server words the user bubble from the action.
      const chatRequest: ChatRequest = surfaceAction
        ? { thread_id: threadId, message: '', prompt_label: promptLabel, surface_action: surfaceAction }
        : { thread_id: threadId, message: messageToSend || '(파일 첨부)', prompt_label: promptLabel };
      if (currentFiles.length > 0) {
        chatRequest.files = currentFiles.map(toFileAttachment);
      }

      await processStreamEvents(chatRequest, aiMsgId);

      // Reload messages from server to get real message IDs (needed for edit_resend)
      if (threadId) await loadThreadMessages(threadId, { skipPendingDetection: true });

      // Refresh memories after chat
      setTimeout(() => {
        if (selectedUserId) tabData.loadUserMemories(selectedUserId);
      }, 2000);
    } catch (err) {
      console.error('Streaming chat error:', err);
      // Pre-stream HTTP errors (ChatHttpError) land here; dispatch through the
      // shared error map so GENERATION_IN_PROGRESS/QUOTA_EXCEEDED/MESSAGE_ARCHIVED
      // etc. trigger the right UX flags (blockSend, cancelEditMode, ...).
      // AbortError is user-initiated cancellation — no toast, just silent exit.
      if (err instanceof ChatHttpError) {
        dispatchByErrorCode(handleChatError, err.errorCode, err.detail, {
          retryAfter: err.retryAfter,
          ...err.extra,
        });
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        // no-op — user pressed 중단
      } else {
        handleChatError(err instanceof Error ? err.message : '메시지 전송에 실패했습니다. 다시 시도해주세요.');
      }
      setMessages((prev) => prev.filter((msg) => msg.id !== aiMsgId));
    } finally {
      setIsStreaming(false);
      setThoughtStep(null);
    }
  };

  const handleEditResend = async () => {
    if (!editingMessageId || !editingContent.trim() || !selectedUserId || !selectedThreadId || isStreaming) return;

    const targetMsgId = editingMessageId;
    const newContent = editingContent.trim();
    const currentFiles = [...attachedFiles];

    // Exit edit mode
    setEditingMessageId(null);
    setEditingContent('');
    setAttachedFiles([]);

    // Remove target message and everything after it, then add edited user + empty AI placeholder
    const aiMsgId = (Date.now() + 1).toString();
    setMessages((prev) => {
      const targetIdx = prev.findIndex((m) => m.id === targetMsgId);
      if (targetIdx === -1) return prev;
      const before = prev.slice(0, targetIdx);
      return [
        ...before,
        {
          ...prev[targetIdx],
          content: newContent,
          source: 'active' as const,
          files:
            currentFiles.length > 0
              ? currentFiles.map((f) => ({
                  name: f.name,
                  media_type: f.media_type,
                  preview: f.preview || undefined,
                }))
              : prev[targetIdx].files,
        },
        { id: aiMsgId, role: 'assistant' as const, content: '', timestamp: new Date(), source: 'active' as const },
      ];
    });

    setIsStreaming(true);
    setError(null);
    setToolCalls([]);

    try {
      const chatRequest: ChatRequest = {
        thread_id: selectedThreadId,
        message: newContent,
        prompt_label: promptLabel,
        action: 'edit_resend',
        edit_target_message_id: targetMsgId,
      };
      if (currentFiles.length > 0) {
        chatRequest.files = currentFiles.map(toFileAttachment);
      }

      await processStreamEvents(chatRequest, aiMsgId);

      // Reload messages from server to get real message IDs
      if (selectedThreadId) await loadThreadMessages(selectedThreadId, { skipPendingDetection: true });

      setTimeout(() => {
        if (selectedUserId) tabData.loadUserMemories(selectedUserId);
      }, 2000);
    } catch (err) {
      console.error('Edit resend error:', err);
      if (err instanceof ChatHttpError) {
        dispatchByErrorCode(handleChatError, err.errorCode, err.detail, {
          retryAfter: err.retryAfter,
          ...err.extra,
        });
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        // no-op — user pressed 중단
      } else {
        handleChatError(err instanceof Error ? err.message : '메시지 수정 재전송에 실패했습니다.');
      }
      // Recover from server state
      if (selectedThreadId) loadThreadMessages(selectedThreadId);
    } finally {
      setIsStreaming(false);
      setThoughtStep(null);
    }
  };

  return (
    <div className="h-[calc(100vh-7rem)] flex gap-4">
      {/* Left Sidebar - User & Thread List */}
      <div className="w-80 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden shrink-0">
        <PlaygroundUserList
          users={users}
          usersLoading={usersLoading}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
          onCreateUser={handleCreateUser}
          onRefreshUsers={loadUsers}
          onDeleteUser={handleDeleteUser}
          onCloneUser={handleCloneUser}
          onTogglePin={handleTogglePinUser}
          onOpenSharing={openSharingDialog}
        />

        {/* Thread section - shown when user is selected */}
        {selectedUserId && (
          <PlaygroundThreadList
            sortedThreads={sortedThreads}
            threads={threads}
            threadsLoading={threadsLoading}
            selectedThreadId={selectedThreadId}
            pinnedThreadIds={pinnedThreadIds}
            cloningThreadId={threadMgr.cloningThreadId}
            onSelectThread={setSelectedThreadId}
            onCreateThread={handleCreateThread}
            onDeleteThread={handleDeleteThread}
            onCloneThread={handleCloneThread}
            onTogglePin={togglePinThread}
            onRename={threadMgr.handleRenameThread}
          />
        )}

        <div className="p-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          총 {users.length}명 사용자
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
        {!selectedUserId ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">사용자를 선택하세요</p>
              <p className="text-sm mt-1">좌측 목록에서 사용자를 선택하거나 새로 생성하세요</p>
              <button
                onClick={handleCreateUser}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus size={16} className="inline mr-1" />새 사용자 생성
              </button>
            </div>
          </div>
        ) : (
          <>
            <TabBar
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                if (tab === 'info') loadAvailableTools();
                if (tab === 'memories' && selectedUserId) tabData.loadUserMemories(selectedUserId);
                if (tab === 'documents' && selectedUserId) tabData.loadUserDocuments(selectedUserId);
                if (tab === 'health' && selectedUserId) tabData.loadHealthAnalyses(selectedUserId);
                if (tab === 'thread') refetchThreadDetail();
              }}
              memoriesCount={tabData.memories.length}
              documentsCount={tabData.documents.length}
              selectedUserId={selectedUserId}
              selectedThreadId={selectedThreadId}
            />

            <ErrorBanner error={error} onDismiss={() => setError(null)} />

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 flex flex-col min-w-0">
                  <VirtualizedMessageList
                    messages={messages}
                    hasMoreArchive={hasMoreArchive}
                    loadingArchive={loadingArchive}
                    onLoadMoreArchive={loadMoreArchive}
                    onCollapseArchive={() => {
                      setMessages((prev) => prev.filter((m) => m.source !== 'archive'));
                      setArchiveOffset(0);
                      setHasMoreArchive(true);
                      if (selectedThreadId) {
                        try {
                          sessionStorage.removeItem(archiveCacheKey(selectedThreadId));
                          sessionStorage.removeItem(archiveMetaCacheKey(selectedThreadId));
                        } catch {}
                      }
                    }}
                    threadSummary={threadSummary}
                    summaryExpanded={summaryExpanded}
                    onToggleSummary={() => setSummaryExpanded(!summaryExpanded)}
                    shouldAutoScroll={!isLoadingArchiveRef.current}
                    emptyTitle="대화를 시작하세요"
                    emptySubtitle={
                      selectedThreadId
                        ? '이 스레드에서 AI와 대화할 수 있습니다'
                        : '메시지를 보내면 자동으로 새 스레드가 생성됩니다'
                    }
                    renderMessage={(msg, msgIdx) => (
                      <ChatMessageRenderer
                        msg={msg}
                        msgIdx={msgIdx}
                        isStreaming={isStreaming}
                        lastToolUiMsgId={lastToolUiMsgId ?? undefined}
                        thoughtStep={thoughtStep ?? undefined}
                        editingMessageId={editingMessageId ?? undefined}
                        editingContent={editingContent}
                        onSetEditingContent={setEditingContent}
                        onStartEdit={handleStartEdit}
                        onCancelEdit={handleCancelEdit}
                        onEditResend={handleEditResend}
                        copiedMessageId={copiedMessageId}
                        onCopy={handleCopyMessage}
                        onSend={handleSend}
                        attachedFiles={attachedFiles}
                        onFileAttach={handleFileAttach}
                        onRemoveFile={removeAttachedFile}
                        toolCalls={toolCalls}
                        onExpandToolPanel={() => {
                          setFocusedToolMessageId(msg.id);
                          setToolPanelExpanded(true);
                        }}
                      />
                    )}
                  />

                  <ChatInputArea
                    input={input}
                    onInputChange={setInput}
                    onSend={handleSend}
                    onAbort={handleAbort}
                    isStreaming={isStreaming}
                    isPendingResponse={isPendingResponse}
                    blockSend={retryAfterBlock}
                    attachedFiles={attachedFiles}
                    onFileAttach={handleFileAttach}
                    onRemoveFile={removeAttachedFile}
                    fileInputRef={fileInputRef}
                    promptLabel={promptLabel}
                    promptLabels={promptLabels}
                    onPromptLabelChange={setPromptLabel}
                    selectedUserId={selectedUserId}
                    onAttachDocumentId={attachDocumentId}
                  />
                </div>

                {/* Tool Call Panel */}
                <ToolCallPanel
                  toolCalls={toolCalls}
                  isExpanded={toolPanelExpanded}
                  currentMessageId={focusedToolMessageId ?? undefined}
                  onToggle={() => {
                    const next = !toolPanelExpanded;
                    setToolPanelExpanded(next);
                    // Toggling the panel resets the focus so re-opening shows
                    // the latest turn's tools, not the last message clicked.
                    if (!next) setFocusedToolMessageId(null);
                  }}
                  onClearFilter={() => setFocusedToolMessageId(null)}
                />

                {/* SSE Event Log Panel — debugging/validation tool */}
                <SSELogPanel
                  entries={sseLog}
                  isExpanded={sseLogExpanded}
                  onToggle={() => setSSELogExpanded(!sseLogExpanded)}
                />
              </div>
            )}

            {/* User Info Tab */}
            {activeTab === 'info' && selectedUser && (
              <InfoTabPanel
                user={selectedUser}
                profile={profile}
                tier={tier}
                threadMetadata={threadDetail?.metadata ?? null}
                extraProfileActions={
                  profile.isEditingProfile ? (
                    <button
                      onClick={applyProfileTemplate}
                      data-testid="template-profile"
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      title="기본 템플릿 적용"
                    >
                      <Sparkles size={12} />
                      템플릿
                    </button>
                  ) : undefined
                }
                extraSections={
                  <AvailableToolsSection
                    tools={availableTools}
                    loading={toolsLoading}
                    onRefresh={loadAvailableTools}
                  />
                }
              />
            )}

            {/* Thread Info Tab */}
            {activeTab === 'thread' && selectedThreadId && (
              <ThreadInfoPanel
                threadDetail={threadDetail ?? null}
                threadDetailLoading={threadDetailLoading}
                threadStats={threadStats}
                threadSummary={threadSummary}
                generationStatus={generationStatus}
                archiveTotal={archiveTotal}
                onRefresh={() => refetchThreadDetail()}
              />
            )}

            {/* Memories Tab */}
            {activeTab === 'memories' && (
              <MemoriesTabPanel
                memories={tabData.memories}
                loading={tabData.memoriesLoading}
                onRefresh={() => selectedUserId && tabData.loadUserMemories(selectedUserId)}
                onDelete={tabData.handleDeleteMemory}
                onUpdate={tabData.handleUpdateMemory}
                history={tabData.memoryHistory}
                historyLoading={tabData.memoryHistoryLoading}
                onLoadHistory={() => selectedUserId && tabData.loadMemoryHistory(selectedUserId)}
              />
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <DocumentsTabPanel
                documents={tabData.documents}
                loading={tabData.documentsLoading || tabData.uploadingFile}
                onRefresh={() => selectedUserId && tabData.loadUserDocuments(selectedUserId)}
                onDelete={tabData.handleDeleteDocument}
                onToggle={tabData.handleToggleDocument}
                expandedDocumentId={tabData.expandedDocumentId}
                expandedDocumentDetail={tabData.expandedDocumentDetail}
                loadingDocumentDetail={tabData.loadingDocumentDetail}
                onToggleDetail={(docId) => selectedUserId && tabData.loadDocumentDetail(selectedUserId, docId)}
                headerExtra={
                  <label className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer flex items-center gap-1">
                    <Upload size={14} />
                    업로드
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.txt,.md,.png,.jpg,.jpeg,.webp,.heic"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) tabData.handleFileUpload(files);
                        e.target.value = '';
                      }}
                      disabled={tabData.uploadingFile}
                    />
                  </label>
                }
              />
            )}

            {/* Health Analysis Tab */}
            {activeTab === 'health' && (
              <HealthTabPanel
                analyses={tabData.healthAnalyses}
                loading={tabData.healthAnalysisLoading}
                expandedAnalysisId={tabData.expandedAnalysisId}
                expandedAnalysisDetail={tabData.expandedAnalysisDetail}
                loadingAnalysisDetail={tabData.loadingAnalysisDetail}
                onRefresh={() => selectedUserId && tabData.loadHealthAnalyses(selectedUserId)}
                onToggleDetail={(analysisId) => selectedUserId && tabData.loadAnalysisDetail(selectedUserId, analysisId)}
                onDelete={tabData.handleDeleteHealthAnalysis}
                emptyMessage="위 폼에서 건강 데이터를 입력하고 분석을 요청하세요"
                beforeList={
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">건강 분석 요청</h4>
                    <p className="text-xs text-slate-500 mb-3">
                      건강 데이터를 JSON 형식으로 입력하세요. (예: 심박수, 수면 시간, 증상 등)
                    </p>
                    <textarea
                      value={healthContext}
                      onChange={(e) => setHealthContext(e.target.value)}
                      data-testid="health-context-input"
                      placeholder={`{
  "심박수": 75,
  "수면시간": 6,
  "증상": "무릎 통증",
  "운동계획": "러닝 5km"
}`}
                      className="w-full h-32 bg-slate-900 text-green-400 p-4 rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={handleRequestHealthAnalysis}
                        disabled={isRequestingAnalysis || !healthContext.trim()}
                        data-testid="request-analysis-button"
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isRequestingAnalysis ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            분석 중...
                          </>
                        ) : (
                          <>
                            <Activity size={14} />
                            분석 요청
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                }
              />
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && selectedUserId && (
              <CalendarTabPanel userId={selectedUserId} />
            )}
          </>
        )}
      </div>

      {/* Sharing Dialog */}
      {sharingDialogUserId && (
        <SharingDialog
          adminUsersList={adminUsersList}
          selectedAdminIds={selectedAdminIds}
          shareToAll={shareToAll}
          sharingLoading={sharingLoading}
          onSave={handleSaveSharing}
          onClose={() => setSharingDialogUserId(null)}
          setSelectedAdminIds={setSelectedAdminIds}
          setShareToAll={setShareToAll}
        />
      )}
    </div>
  );
};

export default PlaygroundChat;
